import {gql} from '@apollo/client';
import {useEffect, useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {AllTagSchemasForProceduresQuery} from 'procedures/utils/__generated/AllTagSchemasForProceduresQuery';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';

import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';

// Put any new TagSchema information (that isn't included in ALL_TAG_SCHEMAS_QUERY) here
export const ALL_TAG_SCHEMAS_FOR_PROCEDURES = gql`
  query AllTagSchemasForProceduresQuery @skipAudit @engComponent(name: PROCEDURES) {
    tagSchemas {
      id
      interfaces {
        id
        name
      }
    }
  }
`;

export interface TagSchemaForProcedures {
  id: string;
  interfaces: Array<SchemaInterfaceSummary>;
}

export function useAllTagSchemasWithProceduresShape(): null | Array<TagSchemaForProcedures> {
  const [tagSchemas, setTagSchemas] = useState<Array<TagSchemaForProcedures> | null>(null);

  const tagSchemasResult = useSkippableBenchlingQuery<AllTagSchemasForProceduresQuery>(
    ALL_TAG_SCHEMAS_FOR_PROCEDURES,
    {
      renderCachedDataWhenRefetching: true,
    }
  );

  useEffect(() => {
    if (tagSchemasResult.type === QueryResultType.SUCCESS) {
      setTagSchemas(tagSchemasResult.data.tagSchemas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagSchemasResult.type]);

  return tagSchemas;
}

export function useAllTagSchemasWithStandardShape(
  onLoaded?: (tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>) => void
): null | Array<TagSchemaListingWithFieldsGraphQL> {
  const [tagSchemas, setTagSchemas] = useState<Array<TagSchemaListingWithFieldsGraphQL> | null>(null);

  // Do NOT change the graphql being queried here! This is an expensive query so we want to rely on Apollo
  // caching. 99% of the time the user already navigated through the benchling settings page, and that page
  // already did the ALL_TAG_SCHEMAS_QUERY so we get it for free. If you want new information put that in
  // useAllTagSchemasWithProceduresShape()
  const tagSchemasResult = useSkippableBenchlingQuery<AllTagSchemasQuery>(ALL_TAG_SCHEMAS_QUERY, {
    renderCachedDataWhenRefetching: true,
  });

  useEffect(() => {
    if (tagSchemasResult.type === QueryResultType.SUCCESS) {
      const ts = tagSchemasResult.data.tagSchemas;
      setTagSchemas(ts);
      onLoaded?.(ts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagSchemasResult.type]);

  return tagSchemas;
}

export function doesSchemaImplementEquipmentInterface(schema: TagSchemaForProcedures): boolean {
  return schema.interfaces.some((ssi) => ssi.id === EQUIPMENT_SCHEMA_INTERFACE_ID);
}
